"use client"

import { ampli } from "@/ampli"
import { Text } from "@/elements/Text"
import { TextLink } from "@/elements/TextLink"
import { FragmentType, graphql, useFragment } from "@/gql"
import { JobApplicationProjectPreviewFragmentDoc, JobPostingPayRate, JobPostingPayType } from "@/gql/graphql"
import type { NovaSession } from "@/types"
import { DocumentNode, useQuery } from "@apollo/client"
import { ArrowLeftIcon } from "@heroicons/react/24/outline"
import { FC, useState } from "react"
import { ApplyToJobContent, ApplyToJobFooter, FormField, ProposedRateForm } from "../ApplyToJob"

import { ApplyToJobSelectWork } from "../ApplyToJobSelectWork/ApplyToJobSelectWork"
import { ApplyToJobSelectWorkFooter } from "../ApplyToJobSelectWork/ApplyToJobSelectWorkFooter"
import { JobApplicationContent } from "../JobApplication/JobApplicationContent"
import { JobApplicationFooter } from "../JobApplication/JobApplicationFooter"
import { AuthenticatedJobDetailsFooter } from "../JobDetails/JobDetailsAuthenticated/AuthenticatedJobDetailsFooter"
import { AuthenticatedJobDetailsContent } from "../JobDetails/JobDetailsAuthenticated/AuthenticatedJobsDetailsContent"
import { JobsSideDrawerTemplate } from "../JobsSideDrawerTemplate"

const JobsBoardSideDrawerFragment = graphql(`
  fragment JobsBoardSideDrawer on JobPosting {
    id
    __typename

    position {
      id
    }

    ...AuthenticatedJobDetailsContentJobPosting
    ...AuthenticatedJobDetailsFooterCreativeViewer
  }
`)

const JobsBoardSideDrawerQuery = graphql(`
  query JobsBoardSideDrawer(
    $jobId: ID!
    $positionId: ID!
    $creativeId: ID!
    $first: Int!
    $after: String
    $category: [ID!]
    $creatorRoles: [ID!]
  ) {
    jobPosting(id: $jobId) {
      id
      __typename
      ... on JobPosting {
        ...ApplyToJobContentJobPosting
        ...ApplyToJobFooter
        ...AuthenticatedJobDetailsContentJobPosting
        ...AuthenticatedJobDetailsFooterCreativeViewer
        ...JobApplicationContentJobPosting
        ...AuthenticatedCreativeJobDetailsFooter
        activeInteractionForCreative(creativeId: $creativeId) {
          ... on JobApplication {
            id
            applicationStatus: status
          }
        }
      }
    }
    individualCreative(id: $creativeId) {
      id
      ...ApplyToJobContentCreative
    }
  }
`)

interface Props {
  serverSession: NovaSession
  onOpenChange: (open: boolean) => void
  open: boolean
  openSubscriptionCheckoutModal: (jobId: string) => void
  jobPostingHasImage?: boolean
  withdrawMutationRefetchQueries: DocumentNode[]
  jobPosting: FragmentType<typeof JobsBoardSideDrawerFragment> | undefined | null
}

export const JobsBoardSideDrawer: FC<Props> = ({
  serverSession,
  onOpenChange,
  open,
  openSubscriptionCheckoutModal,
  jobPostingHasImage = false,
  withdrawMutationRefetchQueries,
  ...props
}) => {
  const jobDetailJobPosting = useFragment(JobsBoardSideDrawerFragment, props.jobPosting)
  const creativeId = serverSession?.user?.creative?.id

  const [coverLetter, setCoverLetter] = useState("")
  const [coverLetterError, setCoverLetterError] = useState<string | undefined>()
  const coverLetterForm: FormField = {
    value: coverLetter,
    error: coverLetterError,
    onChange: setCoverLetter,
    setError: setCoverLetterError,
  }

  const [proposedRate, setProposedRate] = useState("")
  const [proposedRateError, setProposedRateError] = useState<string | undefined>()
  const [payRateFrequency, setPayRateFrequency] = useState<JobPostingPayRate | undefined>(undefined)
  const [payType, setPayType] = useState<JobPostingPayType | undefined>(undefined)
  const proposedRateForm: ProposedRateForm = {
    value: proposedRate,
    error: proposedRateError,
    onChange: setProposedRate,
    setError: setProposedRateError,
    payRateFrequency,
    setPayRateFrequency,
    payType,
    setPayType,
  }

  const [creativeWorkAttachments, setCreativeWorkAttachments] = useState<
    FragmentType<typeof JobApplicationProjectPreviewFragmentDoc>[]
  >([])
  const [view, setView] = useState<"jobDetails" | "apply" | "selectWork" | "viewApplication">("jobDetails")
  const [hasViewedSelectionScreen, setHasViewedSelectionScreen] = useState(false)

  const { data, loading } = useQuery(JobsBoardSideDrawerQuery, {
    variables: {
      jobId: jobDetailJobPosting?.id!,
      positionId: jobDetailJobPosting?.position?.id!,
      creativeId: creativeId!,
      first: 8,
      after: null,
    },
    context: {
      version: 2,
    },
    skip: !jobDetailJobPosting?.id || !creativeId,
    fetchPolicy: "cache-and-network",
  })

  const creative = data?.individualCreative

  const queryJobPosting =
    data?.jobPosting?.__typename === "ClientJobPosting" || data?.jobPosting?.__typename === "CreativeJobPosting"
      ? data?.jobPosting
      : null

  const onApply = () => {
    setView("apply")
  }

  const onClose = () => {
    setView("jobDetails")
    setCreativeWorkAttachments([])
    setCoverLetter("")
    setCoverLetterError(undefined)
    setProposedRate("")
    setProposedRateError(undefined)
    setPayRateFrequency(undefined)
    setPayType(undefined)
    setHasViewedSelectionScreen(false)
    onOpenChange(false)
  }

  const handleBack = () => {
    if (view === "selectWork") {
      setView("apply")
    } else {
      setView("jobDetails")
    }
  }

  return (
    <JobsSideDrawerTemplate
      hideCloseOnMobile={view === "viewApplication" || view === "selectWork" || view === "apply"}
      mainContent={
        <>
          {view !== "jobDetails" && (
            <>
              <div className="relative mt-6 flex h-12 w-full items-center justify-center px-5 md:px-10">
                <TextLink
                  text="Back"
                  LeftIcon={ArrowLeftIcon}
                  onClick={handleBack}
                  className="absolute left-5 md:left-10"
                />
                <Text className="">{view === "selectWork" ? "Select projects" : "Your application"}</Text>
              </div>
            </>
          )}
          {view === "jobDetails" ? (
            <AuthenticatedJobDetailsContent
              jobPosting={jobDetailJobPosting}
              serverSession={serverSession}
              showSkeletonImage={jobPostingHasImage}
              loading={loading}
              openSubscriptionCheckoutModal={openSubscriptionCheckoutModal}
            />
          ) : null}
          {view === "apply" ? (
            <div className="md:px-10">
              <ApplyToJobContent
                jobPosting={queryJobPosting}
                creative={creative}
                loading={loading}
                serverSession={serverSession}
                coverLetter={coverLetterForm}
                proposedRate={proposedRateForm}
                onSelectWork={() => {
                  setView("selectWork")
                  setHasViewedSelectionScreen(true)
                }}
                creativeWorkAttachments={creativeWorkAttachments}
                hasViewedSelectionScreen={hasViewedSelectionScreen}
              />
            </div>
          ) : null}
          {view === "selectWork" ? (
            <div className="md:px-10">
              <ApplyToJobSelectWork
                jobPosting={queryJobPosting}
                creativeId={creative?.id}
                loading={loading}
                creativeWorkAttachments={creativeWorkAttachments}
                setCreativeWorkAttachments={setCreativeWorkAttachments}
              />
            </div>
          ) : null}
          {view === "viewApplication" ? (
            <div className="md:px-10">
              <JobApplicationContent
                jobPosting={queryJobPosting}
                creative={creative}
                loading={loading}
                serverSession={serverSession}
              />
            </div>
          ) : null}
        </>
      }
      footer={
        <>
          {view === "jobDetails" ? (
            <AuthenticatedJobDetailsFooter
              creativeViewerJobPosting={jobDetailJobPosting}
              serverSession={serverSession}
              onApply={onApply}
              loading={loading}
              onViewApplication={() => setView("viewApplication")}
              openSubscriptionCheckoutModal={openSubscriptionCheckoutModal}
            />
          ) : null}
          {view === "apply" ? (
            <ApplyToJobFooter
              creativeWorkAttachments={creativeWorkAttachments}
              coverLetter={coverLetterForm}
              proposedRate={proposedRateForm}
              jobPosting={queryJobPosting}
              serverSession={serverSession}
              onClose={onClose}
              loading={loading}
              entryPoint="job-board"
              goToSelectWork={() => setView("selectWork")}
            />
          ) : null}
          {view === "selectWork" ? (
            <ApplyToJobSelectWorkFooter
              returnToApply={() => {
                setView("apply")
                ampli.selectWorkComplete()
              }}
              attachmentLength={creativeWorkAttachments.length}
            />
          ) : null}
          {view === "viewApplication" ? (
            <JobApplicationFooter
              jobPosting={queryJobPosting}
              loading={loading}
              withdrawMutationRefetchQueries={withdrawMutationRefetchQueries}
              returnToJobDetails={() => setView("jobDetails")}
              serverSession={serverSession}
            />
          ) : null}
        </>
      }
      onClose={onClose}
      open={open}
    />
  )
}
